/* Soles District - Build an Outfit (the homepage module).
   Markup, settings and styles live in sections/sd-fitting-room.liquid. The
   files keep the old fitting-room name; only what the shopper reads changed.
   Shuffle Fit is in assets/sd-fit-shuffle.js - see the handle at the bottom.
   The colour and set rules are in assets/sd-fit-colors.js, published as
   window.SDFitColors - see byColor() for how this file uses them.

   The stage holds the garments and their size runs and nothing else; titles
   and prices live in the list underneath. Sizes are on the stage because they
   decide whether a piece is worth stopping for, and someone not using My sizes
   should be able to tap through and see what is left without scrolling.

   Changing a piece slides the old one out and the new one in from the other
   side: the gesture driving it is a swipe, and a swipe ending in an instant
   swap feels like a glitch rather than a move. See step().

   My sizes filters per slot - tops take letters, bottoms letters and waist,
   shoes house format, accessories nothing. Keeping the groups apart is what
   stops someone picking XL and emptying the shoe row. A slot with nothing in
   the chosen sizes shows everything and says so on its line, rather than going
   blank. The sizes are shared with the full builder at /pages/build-a-fit.

   Colour has to work differently from size. Sizes are variant options, so
   Shopify filters them server-side; colour lives in the shopify.color-pattern
   metafield with no storefront filter switched on, so the matching happens
   here, over a wider slice of the feed - see pagesFor().

   Colour ORDERS a slot rather than filtering it: the chosen colour first, then
   its neighbours on the wheel, then neutrals, and anything else dropped. That
   is what makes "close to this colour, plus neutrals" useful - the shopper
   lands on the pink hoodie and the blacks and whites that go with it are the
   next swipe along rather than absent.

   Three things that had to be got right, the first two learned by getting them
   wrong:

   - Do not match on whether a product's colour LIST contains the colour asked
     for. Nearly every garment here carries a white or black accent, so
     "contains a neutral" matched 60 of 71 tops and the filter did nothing.
     The first fix was to read only the first colour in the list - the one the
     piece reads as from across the room. That worked, but it threw away the
     accents, and the accents are often the reason two pieces go together: a
     bone tee with red lettering is the fit for someone holding red shoes.
     The rule now is base AND accents, with neutrals barred from counting as
     accents - which is what holds the original bug shut, since a black tee
     with a white logo still does not answer to White. That arithmetic lives
     in assets/sd-fit-colors.js; the local colorScore below is the older
     single-colour version, kept only as a fallback for the moment before
     that file has loaded.

   - Neutrals rank below, never merged in. They are the point of the feature,
     but they must not outrank the colour actually asked for.

   - The wheel's light and deep bands do something real but modest. Shopify
     tags colour FAMILY, not shade - every red in the catalogue carries the
     same #F61F1F - so a shade cannot honestly filter. What it can do is read
     the company a colour keeps: Pink with White is lighter than Pink with
     Black. A tiebreak within its band, never across bands. See shadeBonus.

   WHY THE SORT CONTROL IS NOW A BUDGET. The select in the controls row used
   to change the feed's sort order. It was taken over rather than added to,
   because the controls row was already at the limit of what can sit there
   without the module looking like a search form - and because sort was quietly
   working against the matching. The shuffle breaks ties on where a piece sits
   in its slot, which is a popularity signal ONLY while the feed is sorted by
   best sellers; under "price: low to high" that tiebreak starts meaning
   "cheapest", which is not what it claims to be. So the sort is pinned to
   best-selling and the control now sets a ceiling for the whole outfit, which
   is the question people actually arrive with. See wireBudget().

   HOW FAST THE FIRST FIT ARRIVES, which is a real feature and was twice a
   bug. The section sits well below the fold on the homepage, so everything
   here is racing the shopper's scroll rather than the page load.

   - The slots show page one and fetch the rest behind it. Waiting for two
     pages per slot before drawing anything put the first fit at 2.4 seconds;
     page one alone puts it at 1.9, and the extra depth lands a few hundred
     milliseconds later, by which time it is only needed for the NEXT press of
     shuffle. See load() and topUp().

   - The four stage images are loaded EAGERLY, which looks wrong on a
     below-the-fold section and is not. With loading="lazy" the browser quite
     correctly refused to fetch them until they were nearly in view, so the
     outfit was computed, ready and invisible, and scrolling down showed four
     empty boxes filling in one at a time. They carry fetchpriority="low" so
     they queue behind everything above the fold rather than competing with
     it. The thumbnails in the list beneath stay lazy - there are more of
     them, they are small, and nobody is waiting on them.

   Any piece can be dropped from the outfit and put back, and any piece can be
   LOCKED, which keeps the shuffle and the arrows off it - you like the shoes,
   you want everything else re-rolled. A dropped piece leaves the stage
   entirely rather than greying out, because the reason to drop it is to see
   the outfit without it. The total counts every piece still in the outfit
   whether or not its size has been chosen - summing only the sized ones showed
   $90 under four garments, which reads as a bug.

   Adding the outfit opens the cart and leaves the shopper on the page: someone
   who has just built an outfit is mid-browse, and a trip to /cart ends that.

   Five traps worth recording:

   - Do NOT send Accept: application/json when fetching the feed. A storefront
     collection URL with that header makes Shopify answer with its own built-in
     collection JSON and ignore ?view entirely, so you get a perfectly valid
     JSON document with none of the fields this file expects.

   - A slot's background top-up must be generation-stamped. Clearing and
     refilling a slot is synchronous but the top-up is not, so without the
     stamp a quick tap on the size chips appends page two of the OLD search to
     the new one and the slot becomes two different searches stacked. See gen
     in load().

   - A swatch is identified by name AND shade, never name alone. Three bands
     share each colour name, so comparing on the name lit all three at once and
     made clearing a colour impossible.

   - The wheel's swatches are SVG <path> elements, which cannot be real
     buttons. They carry role="button" and tabindex, and the keyboard handler
     below is what makes Enter and Space work - but it is bound only to the
     ones that are not native buttons, because a native button already fires
     click on Enter and binding both toggles the colour twice.

   - /cart/add.js PREPENDS each line it is given, so the cart comes out in the
     reverse of the order sent. The outfit is therefore handed over feet
     first. See the lines array in the add handler.

   - Do NOT ask /cart/add.js for a section this theme has not got. The theme
     has sections/cart-drawer.liquid and sections/cart-icon-bubble.liquid but
     no cart-notification section, so those two are all that may be requested;
     naming a missing one risks failing the add itself. */
(function () {
  var root = document.querySelector('[data-sdfr]');
  if (!root) return;

  var money = new Intl.NumberFormat('en-US', { style: 'currency', currency: root.dataset.currency || 'USD' });
  function price(cents) { return money.format(cents / 100); }
  function esc(s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;'); }

  var rows = [].slice.call(root.querySelectorAll('[data-sdfr-row]'));
  var listEl = root.querySelector('[data-sdfr-list]');
  var totalEl = root.querySelector('[data-sdfr-total]');
  var addEl = root.querySelector('[data-sdfr-add]');
  var sortEl = root.querySelector('[data-sdfr-sort]');
  var addLabel = addEl.textContent;

  var state = rows.map(function () {
    return {
      items: [], i: 0, vid: null, unfiltered: false, nocolor: false,
      off: false, locked: false, recent: [], gen: 0, anim: null
    };
  });
  var sizes = { tops: [], bottoms: [], shoes: [] };

  /* Pinned to best sellers on purpose - see the note in the header about why
     the sort control became the budget. */
  var sort = 'best-selling';

  var color = null;               /* { name, shade, hex, hue, neutral, seg } or null */
  var budget = 0;                 /* cents, 0 for no ceiling */
  var firstLoad = true;
  var COLOR_PAGES = 3;            /* pages of feed to pull per slot when a colour is on */
  var BASE_PAGES = 2;             /* and when it is not - see pagesFor() */
  var SWIPE_MS = 200;             /* must match the transition in the section's CSS */

  /* The shopper's sizes are worth remembering between visits - it is the same
     body next time - and the full builder reads the same key. Colour is not
     stored: it belongs to whatever outfit they are putting together today.
     Wrapped because private mode throws on access. */
  var STORE = 'sd-fitting-room-sizes';
  try {
    var saved = JSON.parse(localStorage.getItem(STORE) || 'null');
    if (saved && saved.tops) sizes = saved;
  } catch (e) { /* no stored sizes, carry on */ }
  function remember() {
    try { localStorage.setItem(STORE, JSON.stringify(sizes)); } catch (e) { /* nothing to do */ }
  }

  /* ---- colour -----------------------------------------------------------
     Everything from here to the end of colorScore is the ORIGINAL
     single-colour matcher. It is no longer the one normally used - see
     byColor - but it is kept rather than deleted because it is the fallback
     for the sliver of time before assets/sd-fit-colors.js has loaded, and
     because segment() is still needed locally when a swatch is picked. */

  /* Names beat hex for these. Brown's own swatch is #9A5630, which is orange
     by hue and would be matched by anyone picking orange and missed by anyone
     wanting earth tones; in a wardrobe it is a neutral. Gold and bronze are
     the same story, and beige's swatch is saturated enough to read as yellow.
     So the name decides first and the hex only settles what the name does not. */
  var NEUTRAL = ('black white gray grey silver charcoal beige brown cream ivory tan khaki taupe ' +
    'off-white offwhite natural bone sand camel chocolate gold bronze copper rose gold clear').split(' ');

  /* The company a colour keeps, used only to order within a band. */
  var LIGHT_COMPANY = ('white cream ivory beige silver natural bone off-white offwhite sand tan gold').split(' ');
  var DEEP_COMPANY = ('black charcoal brown chocolate navy gray grey bronze copper').split(' ');

  /* The wheel is read off the page rather than restated here, so the section
     stays the single place the colours are defined. The middle band only - the
     light and deep bands repeat the same eight hues, and the neutrals sit
     outside the wheel and play by their own rule. */
  var WHEEL = [].slice.call(root.querySelectorAll('[data-sdfr-color][data-neutral="false"][data-shade="base"]'))
    .map(function (sw) { return { name: sw.dataset.name, hue: Number(sw.dataset.hue) }; })
    .sort(function (a, b) { return a.hue - b.hue; });

  function arc(a, b) {
    var d = Math.abs(a - b) % 360;
    return Math.min(d, 360 - d);
  }

  /* Which wedge of the wheel a hue belongs to. Nearest wins, which is what
     makes teal land on blue and olive on green without a lookup table. */
  function segment(hue) {
    if (!WHEEL.length) return -1;
    var best = 0;
    for (var i = 1; i < WHEEL.length; i++) {
      if (arc(hue, WHEEL[i].hue) < arc(hue, WHEEL[best].hue)) best = i;
    }
    return best;
  }

  /* Adjacency is counted in wedges, not degrees. The colours are not evenly
     spaced round the hue circle - this store's purple sits at 281 and its pink
     at 350, while orange and yellow are 22 apart - so any single degree
     threshold either divorces purple from pink or marries red to green. One
     wedge either way is both predictable and what "close to this colour" means
     when you are looking at a wheel. */
  function segNear(a, b) {
    if (a < 0 || b < 0) return false;
    var n = WHEEL.length;
    var d = Math.abs(a - b) % n;
    return Math.min(d, n - d) <= 1;
  }

  function colorName(c) {
    var n = (typeof c === 'string') ? c : (c && c.n);
    return String(n || '').trim().toLowerCase();
  }

  /* Pattern entries share the metafield with the colours - Striped, Animal,
     Plaid - and come through with an empty hex, so they must not be read as a
     colour. Multicolor and Rainbow are the exception: they wear everything, so
     they go with everything. */
  function labelKind(c) {
    /* Tolerates a colour sent as a bare name as well as {n, h}, so a stale
       cached feed degrades to name-only matching instead of matching nothing. */
    var n = colorName(c);
    var hex = (typeof c === 'string') ? '' : (c && c.h);
    if (!n) return { skip: true };
    if (n.indexOf('multi') === 0 || n === 'rainbow') return { any: true };
    if (NEUTRAL.indexOf(n) > -1) return { neutral: true, name: n };
    var h = hexHue(hex);
    if (h === null) return { skip: true };                /* a pattern, or a label with no swatch */
    if (h.neutral) return { neutral: true, name: n };     /* a hex that is effectively grey */
    return { seg: segment(h.hue) };
  }

  /* Hex to hue, with a flag for the colours that have no meaningful hue -
     anything too desaturated, too dark or too light to call. */
  function hexHue(hex) {
    if (!hex) return null;
    var m = String(hex).replace('#', '').trim();
    if (m.length === 3) m = m[0] + m[0] + m[1] + m[1] + m[2] + m[2];
    if (!/^[0-9a-f]{6}$/i.test(m)) return null;
    var r = parseInt(m.slice(0, 2), 16) / 255;
    var g = parseInt(m.slice(2, 4), 16) / 255;
    var b = parseInt(m.slice(4, 6), 16) / 255;
    var max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
    var l = (max + min) / 2;
    var s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
    if (s < 0.2 || l < 0.12 || l > 0.9) return { neutral: true };
    var hue;
    if (d === 0) hue = 0;
    else if (max === r) hue = 60 * (((g - b) / d) % 6);
    else if (max === g) hue = 60 * ((b - r) / d + 2);
    else hue = 60 * ((r - g) / d + 4);
    return { hue: (hue + 360) % 360 };
  }

  /* The colour the piece reads as from across the room: the first real colour
     in its list. Shopify records them main colour first, and that first entry
     matches the colour word in the product title - Blue Fuck Em T-Shirt is
     [Blue, Yellow], Heather Grey Plaid is [Gray, Plaid]. Patterns are skipped
     so a Black + Striped jersey is still a black jersey. */
  function dominant(p) {
    var list = p.colors || [];
    for (var i = 0; i < list.length; i++) {
      var k = labelKind(list[i]);
      if (!k.skip) return k;
    }
    return null;
  }

  /* Does the piece keep the company the chosen shade implies? A pink garment
     tagged Pink + White reads lighter than one tagged Pink + Black. Worth one
     point, never more - see the band arithmetic in colorScore. The middle band
     asks for no shade at all, so it scores nothing here. */
  function shadeBonus(p) {
    if (!color) return 0;
    var want;
    if (color.shade === 'light') want = LIGHT_COMPANY;
    else if (color.shade === 'deep') want = DEEP_COMPANY;
    else return 0;
    var list = p.colors || [];
    for (var i = 0; i < list.length; i++) {
      if (want.indexOf(colorName(list[i])) > -1) return 1;
    }
    return 0;
  }

  /* Bands, doubled so a shade tiebreak can never lift a piece out of its band:
     3 the colour asked for, 2 a neighbour on the wheel, 1 a neutral, 0 drop.
     Doubled that is 6 / 4 / 2, and a near match with the bonus is 5 - still
     under an exact match, which is the whole point. */
  function colorScore(p) {
    var d = dominant(p);
    if (!d) return 0;                /* nothing recorded - cannot claim it matches */
    var band;
    if (d.any) band = 2;             /* multicolor goes with whatever is picked */
    else if (color.neutral) {
      /* A neutral was picked, so only neutrals qualify - that one first. */
      if (!d.neutral) return 0;
      band = d.name === String(color.name).toLowerCase() ? 3 : 1;
    } else if (d.neutral) band = 1;
    else if (d.seg === color.seg) band = 3;
    else if (segNear(d.seg, color.seg)) band = 2;
    else return 0;
    return band * 2 + shadeBonus(p);
  }

  /* ---- the feed --------------------------------------------------------- */
  function slotTypes(row) {
    return (row.dataset.types || '').split(',')
      .map(function (t) { return t.trim(); })
      .filter(function (t) { return t; });
  }

  function slotSizes(row) {
    var g = row.dataset.sizegroup;
    return (g && sizes[g]) ? sizes[g] : [];
  }

  function feedUrl(row, withSizes, page) {
    var url = row.dataset.url + '?view=fitjson&sort_by=' + encodeURIComponent(sort);
    slotTypes(row).forEach(function (t) { url += '&filter.p.product_type=' + encodeURIComponent(t); });
    if (withSizes) {
      slotSizes(row).forEach(function (s) { url += '&filter.v.option.size=' + encodeURIComponent(s); });
    }
    if (page && page > 1) url += '&page=' + page;
    return url;
  }

  function getFeed(url) {
    return fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (t) {
        var data = JSON.parse(t);
        if (!data || !Array.isArray(data.products)) throw new Error('unexpected feed');
        return data;
      });
  }

  function current(n) {
    var s = state[n];
    if (s.off) return null;
    return s.items.length ? s.items[s.i % s.items.length] : null;
  }

  /* A product with one nameless variant is an accessory - no size to ask for,
     so it counts as settled the moment it is on the stage. */
  function plain(p) { return p.variants.length === 1 && p.variants[0].title === 'Default Title'; }

  function chosen(n) {
    var s = state[n];
    var p = current(n);
    if (!p) return null;
    if (plain(p)) return p.variants[0];
    var hit = p.variants.filter(function (v) { return String(v.id) === s.vid; });
    return hit.length ? hit[0] : null;
  }

  function draw(n) {
    var p = current(n);
    rows[n].classList.toggle('is-off', state[n].off);
    rows[n].classList.toggle('is-locked', state[n].locked);
    var media = rows[n].querySelector('[data-sdfr-media]');
    var lock = media.querySelector('.sdfr__lock');
    /* Eager, with low priority - see the note on speed in the header. */
    media.innerHTML = p && p.image
      ? '<img src="' + esc(p.image) + '" alt="" loading="eager" fetchpriority="low">'
      : '';
    /* The lock button is injected by the shuffle file and lives inside the
       media box, which this line has just emptied - so put it back. */
    if (lock) media.appendChild(lock);
  }

  var SWIPE_CLASSES = ['is-leaving-left', 'is-leaving-right', 'is-entering-left', 'is-entering-right'];

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* Move this slot on by one. The piece leaves in the direction of travel and
     its replacement arrives from the other side, so a swipe has somewhere to
     land instead of the garment simply blinking into something else.

     The replacement is positioned with the transition switched off - otherwise
     it would animate out from the centre rather than in from the edge - and
     then released a frame later. Two frames, because one is not enough for
     Safari to have taken the start position before the class comes off. */
  function step(n, by) {
    var s = state[n];
    if (!s.items.length) return;
    /* A locked slot is the shopper saying "this one stays". The arrows and the
       swipe are turned off along with the shuffle, because a lock that only
       half holds is worse than no lock. */
    if (s.locked) return;

    var media = rows[n].querySelector('[data-sdfr-media]');

    var advance = function () {
      s.i = (s.i + by + s.items.length) % s.items.length;
      s.vid = null;               /* a new piece means a new size question */
      draw(n);
      paint();
    };

    /* A second swipe while the first is mid-flight: drop the animation and
       land immediately, rather than leaving a class stuck on the media box. */
    if (s.anim) {
      clearTimeout(s.anim);
      s.anim = null;
      SWIPE_CLASSES.forEach(function (c) { media.classList.remove(c); });
    }

    if (reducedMotion() || !media.querySelector('img')) { advance(); return; }

    var out = by > 0 ? 'is-leaving-left' : 'is-leaving-right';
    var into = by > 0 ? 'is-entering-right' : 'is-entering-left';

    media.classList.add(out);
    s.anim = setTimeout(function () {
      s.anim = null;
      media.classList.remove(out);
      media.classList.add(into);
      advance();
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { media.classList.remove(into); });
      });
    }, SWIPE_MS);
  }

  /* ---- the stage and the list ------------------------------------------- */
  function paint() {
    var total = 0;
    var anything = false;
    var firstUnsized = -1;

    listEl.innerHTML = '';

    rows.forEach(function (row, n) {
      var p = current(n);
      var pick = chosen(n);

      /* The size run under the piece on the stage. Done before the early
         returns below, because a slot that is empty or dropped has to be
         cleared as surely as a full one has to be filled. An accessory has no
         size to offer, so it gets nothing. */
      var stage = row.querySelector('[data-sdfr-stagesizes]');
      if (stage) {
        if (!p || plain(p)) {
          stage.innerHTML = '';
        } else {
          stage.innerHTML = p.variants.map(function (v) {
            return '<button type="button" class="sdfr__stagesize" data-vid="' + v.id + '" aria-pressed="' +
              (pick && String(v.id) === String(pick.id) ? 'true' : 'false') + '">' + esc(v.title) + '</button>';
          }).join('');
          [].forEach.call(stage.querySelectorAll('.sdfr__stagesize'), function (b) {
            b.addEventListener('click', function () {
              state[n].vid = b.dataset.vid;
              paint();
            });
          });
        }
      }

      var item = document.createElement('div');
      item.className = 'sdfr__item';

      if (state[n].off) {
        item.innerHTML =
          '<div class="sdfr__off">' +
            '<span>No ' + esc(row.dataset.label.toLowerCase()) + ' in this outfit</span>' +
            '<button type="button" class="sdfr__addback">Add one</button>' +
          '</div>';
        item.querySelector('.sdfr__addback').addEventListener('click', function () {
          state[n].off = false;
          draw(n);
          paint();
        });
        listEl.appendChild(item);
        return;
      }

      if (!p) return;
      anything = true;

      total += pick ? pick.price : p.price;
      if (!pick && firstUnsized === -1) firstUnsized = n;

      var meta = row.dataset.label;
      if (state[n].locked) meta += ' · locked';
      if (state[n].unfiltered) meta += ' · nothing in your sizes, showing all';
      if (state[n].nocolor) meta += ' · nothing in that color, showing all';

      /* The sizes go on this piece's own row, beside the slot name. The chosen
         one is the pressed chip, so it is not also spelled out in the meta. */
      var sizesHtml = '';
      if (!plain(p)) {
        sizesHtml = '<span class="sdfr__linesizes">' + p.variants.map(function (v) {
          return '<button type="button" class="sdfr__size" data-vid="' + v.id + '" aria-pressed="' +
            (pick && String(v.id) === String(pick.id) ? 'true' : 'false') + '">' + esc(v.title) + '</button>';
        }).join('') + '</span>';
      }

      item.innerHTML =
        '<div class="sdfr__line">' +
          '<span class="sdfr__linethumb">' + (p.image ? '<img src="' + esc(p.image) + '" alt="" loading="lazy">' : '') + '</span>' +
          '<span>' +
            '<a class="sdfr__linetitle" href="' + esc(p.url) + '">' + esc(p.title) + '</a>' +
            '<span class="sdfr__linemeta">' +
              '<span class="sdfr__lineslot">' + esc(meta) + '</span>' +
              sizesHtml +
            '</span>' +
          '</span>' +
          '<span class="sdfr__lineprice">' + price(pick ? pick.price : p.price) + '</span>' +
          '<button type="button" class="sdfr__remove" aria-label="Take the ' + esc(row.dataset.label.toLowerCase()) + ' out of this outfit">&times;</button>' +
        '</div>';

      item.querySelector('.sdfr__remove').addEventListener('click', function () {
        state[n].off = true;
        state[n].vid = null;
        state[n].locked = false;
        draw(n);
        paint();
      });

      [].forEach.call(item.querySelectorAll('.sdfr__size'), function (b) {
        b.addEventListener('click', function () {
          state[n].vid = b.dataset.vid;
          paint();
        });
      });

      listEl.appendChild(item);
    });

    totalEl.textContent = anything ? price(total) : '—';
    /* Over the ceiling they set, so say so on the total rather than silently
       ignoring it. The shuffle works within the budget; swiping by hand does
       not, and should not - this is their outfit. */
    totalEl.classList.toggle('is-over', !!budget && total > budget);

    /* The button carries the next instruction rather than sitting disabled -
       that is what stops a half-finished outfit looking broken. */
    if (!anything) {
      addEl.disabled = true;
      addEl.textContent = addLabel;
      addEl.dataset.mode = 'wait';
    } else if (firstUnsized > -1) {
      addEl.disabled = false;
      addEl.textContent = 'Choose ' + rows[firstUnsized].dataset.label.toLowerCase() + ' size';
      addEl.dataset.mode = 'size';
      addEl.dataset.slot = firstUnsized;
    } else {
      addEl.disabled = false;
      addEl.textContent = addLabel;
      addEl.dataset.mode = 'add';
    }
  }

  /* ---- loading a slot --------------------------------------------------- */

  /* Two pages normally, three when a colour is on.

     It was one page, and one page is 24 products. That is too thin for what
     the shuffle now does with a slot: the hat row came back with the same
     piece four presses out of five, and a matching set only turned up when
     the other half happened to be inside the first 24 of its own slot. Going
     to two roughly doubles both. Three when a colour is on, because a colour
     only a handful of pieces wear would come up empty off a short list. */
  function pagesFor() { return color ? COLOR_PAGES : BASE_PAGES; }

  function usable(p) { return p.variants && p.variants.length && p.image; }

  function byColor(all) {
    /* The scoring is assets/sd-fit-colors.js's job - it reads base AND
       accents, so a bone tee with red lettering answers to Red instead of
       being filed under beige and dropped. The local colorScore is the older
       single-colour version and is used only if that file has not arrived
       yet, which in practice means never: the brain is fetched on page load
       and this runs when a swatch is tapped. Keeping the fallback means a
       failed asset request degrades the wheel rather than emptying it. */
    var brain = window.SDFitColors;
    var scored = [];
    all.forEach(function (p, idx) {
      var s = brain ? brain.colorScore(p, color) : colorScore(p);
      if (s > 0) scored.push({ p: p, s: s, i: idx });
    });
    /* Score first, original position second. The tiebreak keeps the feed's
       best-selling order inside each band, and means this does not lean on
       Array.sort being stable, which the older Safari this module already
       bends over backwards for does not promise. */
    scored.sort(function (a, b) { return b.s - a.s || a.i - b.i; });
    return scored.map(function (x) { return x.p; });
  }

  function settle(n, items) {
    state[n].items = items;
    state[n].vid = null;
    state[n].recent = [];
    /* On the very first load, start each row somewhere different so the
       opening outfit is not three best-sellers in a line. The shuffle then
       replaces it with a real fit as soon as every slot has arrived - see
       the opening fit in assets/sd-fit-shuffle.js. After that the shopper
       has asked for an order, so honour it and start at the top. */
    state[n].i = firstLoad ? Math.floor(Math.random() * Math.max(items.length, 1)) : 0;
    draw(n);
    paint();
  }

  /* The pages after the first, fetched once the slot is already on screen and
     appended rather than replacing. Appending is safe because state.i indexes
     from the front, so whatever is showing stays showing. Skipped when a
     colour is on: those pages have to be scored against the whole slice
     together, so they are fetched up front instead. */
  function topUp(n, withSizes, gen, lastPage) {
    if (color || lastPage < 2) return;
    for (var pg = 2; pg <= lastPage; pg++) {
      getFeed(feedUrl(rows[n], withSizes, pg))
        .then(function (d) {
          if (state[n].gen !== gen) return;      /* a newer load owns this slot */
          var more = d.products.filter(usable);
          if (more.length) state[n].items = state[n].items.concat(more);
        })
        .catch(function () { /* fewer pieces to swipe through, not an error */ });
    }
  }

  function load(n, withSizes) {
    var row = rows[n];
    var want = pagesFor();
    var gen = ++state[n].gen;

    getFeed(feedUrl(row, withSizes, 1))
      .then(function (first) {
        if (state[n].gen !== gen) return;
        var last = Math.min(want, first.pages || 1);
        var page1 = first.products.filter(usable);

        /* A slot with nothing in the chosen sizes shows everything instead of
           going blank, and owns up to it on its line. Sizes first, because a
           size miss is the stricter one. */
        if (!page1.length && withSizes && slotSizes(row).length) {
          state[n].unfiltered = true;
          load(n, false);
          return;
        }
        if (withSizes) state[n].unfiltered = false;

        /* No colour: draw page one now, fetch the depth behind it. */
        if (!color) {
          state[n].nocolor = false;
          settle(n, page1);
          topUp(n, withSizes, gen, last);
          return;
        }

        /* A colour IS on, so every page has to be in hand before any of it
           can be ordered - a half-scored slot would put page one's neutrals
           above page two's exact matches. */
        var more = [];
        for (var pg = 2; pg <= last; pg++) more.push(getFeed(feedUrl(row, withSizes, pg)));
        return Promise.all(more.map(function (q) { return q.catch(function () { return null; }); }))
          .then(function (rest) {
            if (state[n].gen !== gen) return;
            var all = page1.slice();
            rest.filter(Boolean).forEach(function (d) { all = all.concat(d.products.filter(usable)); });
            var items = byColor(all);
            /* Same courtesy as sizes: rather than an empty row, show the slot
               unordered and say which filter it had to let go of. */
            state[n].nocolor = !items.length;
            settle(n, items.length ? items : all);
          });
      })
      .catch(function () {
        listEl.innerHTML = '<p class="sdfr__empty">Could not load these right now. Please refresh.</p>';
      });
  }

  function reload() {
    rows.forEach(function (row, n) {
      state[n].unfiltered = false;
      state[n].nocolor = false;
      load(n, true);
    });
    firstLoad = false;
  }

  /* ---- the budget ------------------------------------------------------- */

  /* The sort select, taken over. See the header for why. The tiers line up
     with how the outfits actually price out here - four pieces land around
     $600-700 at full price - so $500 is a real constraint, $750 is a nudge
     and $1,000 is close to "any" while still matching the package tiers. */
  function wireBudget() {
    if (!sortEl) return;
    var label = root.querySelector('.sdfr__sortlabel');
    if (label) label.textContent = 'Budget';

    var tiers = [
      { v: 0, t: 'Any' },
      { v: 50000, t: 'Under $500' },
      { v: 75000, t: 'Under $750' },
      { v: 100000, t: 'Under $1,000' }
    ];
    sortEl.innerHTML = tiers.map(function (o) {
      return '<option value="' + o.v + '">' + o.t + '</option>';
    }).join('');

    sortEl.addEventListener('change', function () {
      budget = Number(sortEl.value) || 0;
      /* No reload: the budget does not change WHICH pieces a slot holds, only
         which of them the shuffle will reach for. Reloading four slots to
         answer a dropdown would be a second of spinning for nothing. */
      paint();
    });
  }

  /* ---- My sizes --------------------------------------------------------- */
  var countEl = root.querySelector('[data-sdfr-sizecount]');

  function paintSizes() {
    [].forEach.call(root.querySelectorAll('[data-sdfr-size]'), function (chip) {
      var g = chip.dataset.sdfrSize;
      chip.setAttribute('aria-pressed', sizes[g].indexOf(chip.dataset.value) > -1 ? 'true' : 'false');
    });
    [].forEach.call(root.querySelectorAll('[data-sdfr-sizeall]'), function (chip) {
      chip.setAttribute('aria-pressed', sizes[chip.dataset.sdfrSizeall].length ? 'false' : 'true');
    });
    if (countEl) {
      var n = sizes.tops.length + sizes.bottoms.length + sizes.shoes.length;
      countEl.textContent = n ? ' · ' + n : '';
    }
  }

  [].forEach.call(root.querySelectorAll('[data-sdfr-size]'), function (chip) {
    chip.addEventListener('click', function () {
      var g = chip.dataset.sdfrSize;
      var v = chip.dataset.value;
      var at = sizes[g].indexOf(v);
      if (at > -1) sizes[g].splice(at, 1); else sizes[g].push(v);
      paintSizes();
      remember();
      reload();
    });
  });

  [].forEach.call(root.querySelectorAll('[data-sdfr-sizeall]'), function (chip) {
    chip.addEventListener('click', function () {
      sizes[chip.dataset.sdfrSizeall] = [];
      paintSizes();
      remember();
      reload();
    });
  });

  /* ---- the colour wheel ------------------------------------------------- */
  var colorNameEl = root.querySelector('[data-sdfr-colorname]');
  var colorDotEl = root.querySelector('[data-sdfr-colordot]');

  /* Name and shade together. Three bands share each name, so the name alone
     lights all three and nothing can be switched off again. */
  function swatchKey(sw) { return sw.dataset.name + '|' + (sw.dataset.shade || 'base'); }
  function colorKey() { return color ? color.name + '|' + color.shade : null; }

  function paintColor() {
    var key = colorKey();
    [].forEach.call(root.querySelectorAll('[data-sdfr-color]'), function (sw) {
      sw.setAttribute('aria-pressed', key && swatchKey(sw) === key ? 'true' : 'false');
    });
    var clear = root.querySelector('[data-sdfr-colorclear]');
    if (clear) clear.setAttribute('aria-pressed', color ? 'false' : 'true');
    if (colorNameEl) colorNameEl.textContent = color ? ' · ' + color.name : '';
    if (colorDotEl) {
      colorDotEl.style.background = color ? color.hex : '';
      colorDotEl.style.display = color ? '' : 'none';
    }
  }

  function pickSwatch(sw) {
    /* Tapping the swatch you already have on takes it off again - the swatch
       is the control, so it should be able to undo itself. */
    if (colorKey() === swatchKey(sw)) {
      color = null;
    } else {
      var neutral = sw.dataset.neutral === 'true';
      color = {
        name: sw.dataset.name,
        shade: sw.dataset.shade || 'base',
        hex: sw.dataset.hex,
        hue: Number(sw.dataset.hue),
        neutral: neutral,
        seg: neutral ? -1 : segment(Number(sw.dataset.hue))
      };
    }
    paintColor();
    reload();
  }

  [].forEach.call(root.querySelectorAll('[data-sdfr-color]'), function (sw) {
    sw.addEventListener('click', function () { pickSwatch(sw); });

    /* The wheel's wedges are SVG paths wearing role="button", so the keyboard
       has to be wired by hand. Native buttons are left alone: they already
       fire click on Enter, and binding this as well would toggle twice. */
    if (sw.tagName.toLowerCase() !== 'button') {
      sw.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          pickSwatch(sw);
        }
      });
    }
  });

  var colorClear = root.querySelector('[data-sdfr-colorclear]');
  if (colorClear) {
    colorClear.addEventListener('click', function () {
      color = null;
      paintColor();
      reload();
    });
  }

  /* ---- the two drop-down panels ----------------------------------------- */

  /* Only one open at a time. Two open panels push the stage below the fold,
     and the stage is the thing being filtered. */
  function wirePanel(toggleSel, panelSel, caretSel) {
    var toggle = root.querySelector(toggleSel);
    var panel = root.querySelector(panelSel);
    if (!toggle || !panel) return null;
    var caret = caretSel ? root.querySelector(caretSel) : null;
    return {
      close: function () {
        toggle.setAttribute('aria-expanded', 'false');
        panel.hidden = true;
        if (caret) caret.innerHTML = '&#9662;';
      },
      open: function () {
        toggle.setAttribute('aria-expanded', 'true');
        panel.hidden = false;
        if (caret) caret.innerHTML = '&#9652;';
      },
      isOpen: function () { return toggle.getAttribute('aria-expanded') === 'true'; },
      toggleEl: toggle
    };
  }

  var sizePanel = wirePanel('[data-sdfr-sizetoggle]', '[data-sdfr-sizepanel]', '[data-sdfr-sizecaret]');
  var colorPanel = wirePanel('[data-sdfr-colortoggle]', '[data-sdfr-colorpanel]', '[data-sdfr-colorcaret]');

  [[sizePanel, colorPanel], [colorPanel, sizePanel]].forEach(function (pair) {
    var me = pair[0], other = pair[1];
    if (!me) return;
    me.toggleEl.addEventListener('click', function () {
      if (me.isOpen()) { me.close(); return; }
      if (other) other.close();
      me.open();
    });
  });

  /* ---- to the cart ------------------------------------------------------ */

  /* The theme's own <cart-drawer> is the cart now - UpCart was switched off in
     October 2026 when the drawer was rebuilt in the theme. The UpCart branch is
     kept because the app is still installed and could be re-enabled from the
     admin without this file knowing, and because it costs one typeof check. A
     plain trip to /cart is the last resort rather than the normal path.

     Wrapped at every step: renderContents throws if the cart icon bubble is
     missing. A dead button is a worse outcome than a page change. */
  function showCart(payload) {
    if (typeof window.upcartOpenCart === 'function') {
      var open = function () {
        try { window.upcartOpenCart(); } catch (e) { /* nothing more to try */ }
      };
      try {
        Promise.resolve(window.upcartRefreshCart ? window.upcartRefreshCart() : null).then(open, open);
      } catch (e) {
        open();
      }
      return true;
    }

    var el = document.querySelector('cart-drawer');
    if (!el || typeof el.renderContents !== 'function') return false;
    if (!payload || !payload.sections) return false;
    try {
      el.classList.remove('is-empty');
      el.renderContents(payload);
      return true;
    } catch (e) {
      return false;
    }
  }

  addEl.addEventListener('click', function () {
    if (addEl.dataset.mode === 'size') {
      var items = listEl.querySelectorAll('.sdfr__item');
      var target = items[Number(addEl.dataset.slot)];
      if (target) target.scrollIntoView({ block: 'center' });
      return;
    }

    /* Handed over FEET FIRST - shoes, bottom, top, hat - which is the reverse
       of the stage. /cart/add.js prepends each line it is given, so whatever
       goes last ends up at the top of the cart. Sent head first the outfit
       arrived upside down and the shoes led. unshift, not push. */
    var lines = [];
    rows.forEach(function (row, n) {
      var pick = chosen(n);
      if (pick) lines.unshift({ id: Number(pick.id), quantity: 1 });
    });
    if (!lines.length) return;

    addEl.disabled = true;
    addEl.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: lines,
        /* Both of these sections exist in this theme; do not add one that
           does not - see the trap in the header. */
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname
      })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed');
        return r.json();
      })
      .then(function (payload) {
        if (!showCart(payload)) {
          window.location.href = '/cart';
          return;
        }
        /* The outfit stays on the stage: they may well want to build another,
           and the pieces they just bought are the obvious starting point. */
        paint();
        addEl.textContent = 'Added to cart ✓';
        addEl.disabled = true;
        setTimeout(paint, 2200);
      })
      .catch(function () {
        paint();
        alert('Could not add that outfit to the cart. One of the sizes may have just sold out - try picking it again.');
      });
  });

  /* ---- wiring ----------------------------------------------------------- */
  rows.forEach(function (row, n) {
    row.querySelector('[data-sdfr-prev]').addEventListener('click', function () { step(n, -1); });
    row.querySelector('[data-sdfr-next]').addEventListener('click', function () { step(n, 1); });

    /* Swipe across a piece to change it - the gesture the layout invites. */
    var media = row.querySelector('[data-sdfr-media]');
    var x0 = null;
    media.addEventListener('touchstart', function (e) { x0 = e.changedTouches[0].clientX; }, { passive: true });
    media.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var dx = e.changedTouches[0].clientX - x0;
      x0 = null;
      if (Math.abs(dx) > 40) step(n, dx < 0 ? 1 : -1);
    }, { passive: true });
  });

  /* ---- the handle for Shuffle Fit ---------------------------------------
     assets/sd-fit-shuffle.js needs the per-slot state and the colour helpers
     closed over above. It would belong in this file, but this file is already
     at the size where the theme write tooling refuses it, and the shuffle is a
     separate idea with rules of its own. So the few pieces it uses are handed
     out on the root element - a module handle, not a global, and nothing else
     reads it. Keep this in step with what that file destructures. */
  root.sdfr = {
    rows: rows,
    state: state,
    SWIPE: SWIPE_CLASSES,
    current: current,
    draw: draw,
    paint: paint,
    plain: plain,
    dominant: dominant,
    segNear: segNear,
    slotSizes: slotSizes,
    reducedMotion: reducedMotion,
    color: function () { return color; },
    budget: function () { return budget; }
  };

  wireBudget();
  paintSizes();
  paintColor();
  reload();
})();
